import {ChangeDetectionStrategy, Component, effect, inject, Input, OnInit, ViewChild} from '@angular/core';
import {InputComponent} from "../../shared/components/controls/form-controls/input/input.component";
import {TextareaComponent} from "../../shared/components/controls/form-controls/textarea/textarea.component";
import {FormArray, FormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {I18NextPipe} from "@kokos-i18next";
import {HeaderComponent} from "../../core/components/header/header.component";
import {SectionComponent} from "../../shared/components/structure/section/section.component";
import {FooterComponent} from "../../shared/components/footer/footer.component";
import {AnhangMetadaten, NachrichtPayload} from "../../core/model/nachricht/nachricht";
import {PagesEnum} from "../../core/model/pages.enum";
import {NavigationService} from "../../shared/utils/navigation.service";
import i18next from "i18next";
import {FooterButton, FooterButtonType} from "../../shared/components/footer/footer-button";
import {DatenschutzComponent} from "./datenschutz/datenschutz.component";
import {KenntnisnahmeComponent} from "./kenntnisnahme/kenntnisnahme.component";
import {UserAccountService} from "../../core/services/user-account.service";
import {HinweisboxTypeEnum} from "../../shared/components/hinweisbox/hinweisbox-type.enum";
import {FieldDisplayComponent} from "../../shared/components/structure/field-display/field-display.component";
import {NachrichtFileUploaderComponent} from "./nachricht-file-uploader/nachricht-file-uploader.component";
import {NachrichtFormGroup, UploadFileFG} from "../../core/model/nachricht-form";
import {UploadFormFactory} from "./upload-form-factory";
import {stringLatinValidator} from "../../shared/validators/string-latin.validator";
import {
    MAX_LENGTH,
    REQUIRED,
    STRING_LATIN,
    ValidationMessagesService
} from "../../shared/components/structure/validation-message/validation-message.service";
import {ValidationMessage} from "../../shared/components/structure/validation-message/validation-message";
import {ErrorboxComponent} from "./errorbox/errorbox.component";
import {ErrorType} from "./errorbox/error-types";
import {ScrollIntoView} from "../../shared/directives/scroll-into-view.directive";
import {NachrichtFormFocusService} from "./nachricht-form-focus.service";
import {NachrichtenService} from "../../core/services/apiservices/nachrichten.service";
import {ModalComponent} from "../../shared/components/modal/modal.component";
import {NachrichtVerlaufComponent} from "./nachricht-verlauf/nachricht-verlauf.component";
import {HistorienElement} from "../../core/model/historien-element";
import {NachrichtSuccessType} from "../nachricht-success-type.enum";
import {finalize} from 'rxjs/operators';
import {KinderLeistung} from "../../core/model/kinder-leistung";
import {RedirectService} from "../../core/services/redirect.service";


@Component({
    selector: 'kokos-nachricht-form',
    imports: [
        DatenschutzComponent,
        ErrorboxComponent,
        FieldDisplayComponent,
        FooterComponent,
        HeaderComponent,
        I18NextPipe,
        InputComponent,
        KenntnisnahmeComponent,
        NachrichtFileUploaderComponent,
        ReactiveFormsModule,
        ScrollIntoView,
        SectionComponent,
        TextareaComponent,
        ModalComponent,
        NachrichtVerlaufComponent,

    ],
    templateUrl: './nachricht-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtFormComponent implements OnInit {

    @Input() nachrichtPayload: NachrichtPayload;
    @Input() anhangMetadaten: AnhangMetadaten[];
    @Input() anliegenName: string;
    @Input() subanliegenName: string;
    @Input() showUpload = false;
    @Input() betreffEditable = true;
    @Input() nachrichtHistorie: HistorienElement[];

    @ViewChild('cancelModal', {static: false}) cancelModal: ModalComponent;

    validationEnabled = false;
    processing = false;
    errorType: ErrorType | null = null;
    errorCode: string;

    protected readonly PagesEnum = PagesEnum;
    protected readonly KinderLeistung = KinderLeistung;
    protected readonly HinweisboxTypeEnum = HinweisboxTypeEnum;

    validationMessages: ValidationMessage[];

    form!: NachrichtFormGroup;
    private readonly fb = inject(FormBuilder);
    private readonly nav = inject(NavigationService);
    private readonly userAccountService = inject(UserAccountService);
    private readonly nachrichtenService = inject(NachrichtenService);
    private readonly uploadFormFactory = inject(UploadFormFactory);
    private readonly validationMessagesService = inject(ValidationMessagesService);
    private readonly nachrichtFormFocusService = inject(NachrichtFormFocusService);
    private readonly redirectService = inject(RedirectService);

    readonly WHITESPACE = ' ';
    redirectUrl: string;

    constructor() {
        effect(() => {
            this.validationMessagesService.languageChanged();
            this.validationMessages = this.validationMessagesService.getMessages([REQUIRED, STRING_LATIN, MAX_LENGTH]);
        });
    }

    ngOnInit(): void {
        this.redirectUrl = this.redirectService.redirectUrl;
        this.generateForm();
    }

    // === Button-Configs ===

    get storeButton(): FooterButton {
        return {
            label: i18next.t('navigation.safe-as-draft'),
            type: FooterButtonType.store,
            disabled: this.processing
        };
    }

    get forwardButton(): FooterButton {
        return {
            label: i18next.t('navigation.send-message'),
            type: FooterButtonType.forward,
            disabled: this.processing
        };
    }

    get backButton(): FooterButton {
        return {
            label: i18next.t('navigation.cancel'),
            type: FooterButtonType.back,
            disabled: this.processing
        };
    }

    // === User-Checks ===

    isKindergeldFall(): boolean {
        return this.userAccountService.isKindergeldFall();
    }

    isStepUser(): boolean {
        return this.userAccountService.isStepUser();
    }

    isZuwanderungsinteressierter(): boolean {
        return this.userAccountService.isZuwanderungsinteressierter();
    }

    // === Actions ===

    handleForwardClicked(): void {
        if (this.processing) return;

        this.processing = true;
        this.validationEnabled = true;

        if (this.form.valid) {
            const normBetreff = this.normalize(this.form.controls.betreff.value);
            const normText = this.normalize(this.form.controls.text.value);
            Object.assign(this.nachrichtPayload, {betreff: normBetreff, text: normText});
            this.sendNachricht(); // processing wird im finalize() zurückgesetzt
        } else {
            this.nachrichtFormFocusService.focusFirstInvalid(this.form.controls);
            this.processing = false;
        }
    }

    handleStoreClicked() {
        if (this.processing) return;

        this.processing = true;
        this.validationEnabled = true;

        const normBetreff = this.normalize(this.form.controls.betreff.value);
        const normText = this.normalize(this.form.controls.text.value);
        this.form.patchValue({betreff: normBetreff, text: normText});
        Object.assign(this.nachrichtPayload, {betreff: normBetreff, text: normText});

        if (this.form.valid) {
            this.storeDraft(false); // processing im finalize()
        } else {
            this.nachrichtFormFocusService.focusFirstInvalid(this.form.controls);
            this.processing = false;
        }
    }

    // wird aufgerufen, wenn ein Anhang gespeichert werden soll, aber kein Entwurf existiert
    onCreateEntwurf(): void {
        const min = this.normalize();
        this.nachrichtPayload.betreff = min;
        this.nachrichtPayload.text = min;
        this.storeDraft(true);
    }

    onConfirmCancelNachricht(): void {
        if (this.redirectUrl) {
            globalThis.location.href = this.redirectUrl;
        } else {
            this.nav.goTo('/');
        }
    }

    openCancelModal(): void {
        this.cancelModal.open();
    }

    // === Form & Helpers ===

    get anhaengeFormArray(): FormArray<UploadFileFG> {
        return this.form.controls.uploadFile;
    }

    get showSubanliegen(): boolean {
        return this.subanliegenName && this.subanliegenName !== this.anliegenName;
    }

    private normalize(input?: string): string {
        const trimmed = input?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : this.WHITESPACE;
    }

    private generateForm() {
        this.form = this.fb.group({
            betreff: [this.nachrichtPayload.betreff, this.betreffEditable ? [Validators.required, stringLatinValidator(false)] : []],
            text: [this.nachrichtPayload.text, [Validators.required, stringLatinValidator(true)]],
            uploadFile: this.fb.array([] as UploadFileFG[]),
            kenntnisnahme: [false, [Validators.required]]
        });

        if (this.anhangMetadaten) {
            this.anhangMetadaten.forEach(meta =>
                this.anhaengeFormArray.push(this.uploadFormFactory.createSuccess(meta.dateiname, meta.anhangId))
            );
        }
    }

    // === HTTP ===

    private sendNachricht(): void {
        this.errorType = null;

        this.nachrichtenService.sendNachricht(this.nachrichtPayload)
            .pipe(finalize(() => {
                this.processing = false;
            }))
            .subscribe({
                next: () => {
                    this.nav.goTo(['erfolgreich', NachrichtSuccessType.SENT]);
                },
                error: (err) => {
                    if (err.status === 422) {
                        this.errorType = ErrorType.SEND_VALIDATION_ERROR;
                        this.errorCode = err.error.fehler_id;
                    } else {
                        this.errorType = ErrorType.SEND_GENERIC_ERROR;
                    }
                }
            });
    }

    private storeDraft(autoSave: boolean) {
        this.errorType = null;
        this.nachrichtenService.saveEntwurf(this.nachrichtPayload)
            .pipe(finalize(() => {
                //OPOBD-101663: "processing" (verhindert senden) wird nur dann auf false gesetzt, wenn das Speichern des Entwurfs durch ButtonClick getriggert wurde.
                if (!autoSave) {
                    this.processing = false;
                }
            }))
            .subscribe({
                next: (entwurfId) => {
                    this.nachrichtPayload.entwurfId = entwurfId;
                    if (!autoSave) {
                        this.nav.goTo(['erfolgreich', NachrichtSuccessType.SAVED]);
                    }
                },
                error: (err) => {
                    if (err.status === 422) {
                        this.errorType = ErrorType.SAVE_VALIDATION_ERROR;
                        this.errorCode = err.error.fehler_id;
                    } else {
                        this.errorType = ErrorType.SAVE_GENERIC_ERROR;
                    }
                }
            });
    }
}